import type Phaser from "phaser";
import type { TextDirection } from "@yjsgames/i18n";
import { Dropdown } from "./Dropdown.js";
import { cycleOrToggle, type SortState } from "./sortFilter.js";

export interface SortFilterControlOptions<K extends string> {
  readonly width: number;
  /** 정렬 키 목록(드롭다운 옵션 순서). */
  readonly keys: readonly K[];
  /** 초기 정렬 상태. */
  readonly initial: SortState<K>;
  /** 키 → 기본 표시 라벨(방향 화살표 제외). 컨트롤이 현재 키에 ↑/↓를 덧붙인다. */
  readonly labelOf: (key: K) => string;
  /** 정렬 변경(키 선택/방향 토글) 콜백. */
  readonly onChange: (state: SortState<K>) => void;
  readonly dir?: TextDirection;
  /** 트리거 버튼 높이(px) — 기본 36. 헤더 행에서 주변 설명 텍스트와 높이 통일 시 낮춘다(Dropdown으로 전달). */
  readonly triggerHeight?: number;
}

/**
 * 재사용 정렬 컨트롤 — 기존 제네릭 {@link Dropdown}을 래핑해 "정렬 키 선택 + 같은 키 재선택 시 오름↔내림 토글"
 * UX를 한 컴포넌트로 묶는다. 순수 결정 로직은 {@link cycleOrToggle}/SortState(sortFilter)에 위임 —
 * 시각(드롭다운)만 담당한다. 어떤 탭뷰(팔레트·합성/융합 목록 등)에서도 keys/labelOf/onChange만 바꿔 재사용.
 *
 * 현재 선택 키에는 라벨 뒤에 방향 글리프(↑=asc/↓=desc)를 표시한다.
 */
export class SortFilterControl<K extends string> {
  private readonly dropdown: Dropdown<K>;
  private state: SortState<K>;
  private readonly baseLabelOf: (key: K) => string;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: SortFilterControlOptions<K>) {
    this.state = opts.initial;
    this.baseLabelOf = opts.labelOf;
    this.dropdown = new Dropdown<K>(scene, x, y, {
      width: opts.width,
      options: opts.keys,
      current: opts.initial.key,
      labelOf: (k) => this.decorate(k),
      onSelect: (k) => {
        this.state = cycleOrToggle(this.state, k);
        this.dropdown.setCurrent(this.state.key); // 토글이면 no-op, 키 전환이면 trigger 라벨 갱신
        this.dropdown.refreshLabels(); // 방향 화살표 재반영(상태 갱신 후)
        opts.onChange(this.state);
      },
      dir: opts.dir,
      triggerHeight: opts.triggerHeight,
    });
    scene.add.existing(this.dropdown);
    this.dropdown.setDepth(6); // 콘텐츠(grid/palette 2~5) 위 — 펼친 옵션이 타일에 가리지 않게
  }

  /** 현재 키면 방향 글리프(↑/↓)를 덧붙인 라벨. */
  private decorate(key: K): string {
    const arrow = key === this.state.key ? (this.state.dir === "asc" ? " ↑" : " ↓") : "";
    return `${this.baseLabelOf(key)}${arrow}`;
  }

  /** 현재 정렬 상태. */
  get current(): SortState<K> {
    return this.state;
  }

  /** 드롭다운 트리거 위치를 (x, y)로 이동한다. */
  setPosition(x: number, y: number): void {
    this.dropdown.setPosition(x, y);
  }

  /** 컨트롤(트리거 + 펼친 목록) 가시성 설정. 탭 숨김/표시 시 탭뷰가 호출한다. */
  setVisible(visible: boolean): void {
    this.dropdown.setVisible(visible);
  }

  /** 언어 변경 — 라벨 재구성(체크박스/화살표 유지). */
  applyLanguage(): void {
    this.dropdown.refreshLabels();
  }

  /** 텍스트 방향 변경(RTL/LTR) — 화살표 위치를 Dropdown에 전달한다. */
  setDirection(dir: TextDirection): void {
    this.dropdown.setDirection(dir);
  }
}
